import { preventDefault, registerView } from "@frui.ts/views";
import type { ViewComponent } from "@frui.ts/views";
import { observer } from "mobx-react-lite";
import React from "react";
import SubLedgerNewReportViewModel from "../../../viewModels/finance/subLedger/SubLedgerNewReportViewModel";
import ButtonToolbar from "react-bootstrap/ButtonToolbar";
import Button from "@edaz/edazkit/Button/index";
import HeaderRow from "@edaz/edazkit/HeaderRow/index";
import Form from "react-bootstrap/Form";
import { formatNumber, translateGeneral } from "../../../utils/localizations";
import { MainParameters } from "./filters/mainParameters";
import { OrderParameters } from "./filters/orderParameters";
import { ChargeParameters } from "./filters/chargeParameters";
import { TransferParameters } from "./filters/transferParameters";
import { CardParameters } from "./filters/cardParameters";
import { SummaryParameters } from "./filters/summaryParameters";
import LoaderOverlay from "@edaz/edazkit/LoaderOverlay";
import { Modal } from "react-bootstrap";
import Icon, { IconType } from "@edaz/edazkit/Icon";

export const SubLedgerNewReportView: ViewComponent<SubLedgerNewReportViewModel> = observer(({ vm }) => {
  let resetButton;

  if (vm.isDirty) {
    resetButton = (
      <Button type="reset" size="sm" variant="outline-danger">
        {translateGeneral("resetFilter")}
      </Button>
    );
  }

  return (
    <LoaderOverlay loading={vm.busyWatcher.isBusy}>
      <HeaderRow level={3} title={vm.name} className="mb-4">
        <ButtonToolbar>
          <Button variant="light" className="rounded" size="sm" onClick={() => vm.onCloseClick?.()}>
            {vm.translate("close")}
          </Button>
        </ButtonToolbar>
      </HeaderRow>

      <Form className="mb-4" onSubmit={preventDefault(vm.submit)} onReset={preventDefault(vm.reset)} noValidate={true}>
        <MainParameters vm={vm} />
        <OrderParameters vm={vm} />
        <ChargeParameters vm={vm} />
        <TransferParameters vm={vm} />
        <CardParameters vm={vm} />
        <SummaryParameters vm={vm} />

        <div className="col-lg-auto">
          <ButtonToolbar className="mt-2">
            <Button type="submit" size="sm" disabled={!vm.canGenerateReport}>
              {vm.translate("generateReport")}
            </Button>

            {resetButton}
          </ButtonToolbar>
        </div>
      </Form>

      <Modal show={vm.showSuccessModal} dialogClassName="modal-sm" centered>
        <Modal.Body>
          <div className="text-center py-4">
            <Icon icon={IconType.Processing} width={80} height={80} style={{ color: "#cad2da", marginBottom: "1rem" }} />
            <h4 className="mb-4">{vm.translate("successDialog.title")}</h4>
            <p className="mb-4">{vm.translate("successDialog.description")}</p>
            <Button size="sm" onClick={vm.closeSuccessModal} style={{ paddingLeft: "6rem", paddingRight: "6rem" }}>
              {vm.translate("successDialog.done")}
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <Modal show={vm.previewFailedTooManyRows !== undefined} dialogClassName="modal-sm" centered>
        <Modal.Body>
          <div className="text-center py-4">
            <Icon icon={IconType.CircleWarning} width={66} height={66} style={{ color: "#c3113c", marginBottom: "1.8rem" }} />
            <h4 className="mb-4">{vm.translate("previewFailedModal.title")}</h4>
            <p className="mb-4">
              {vm.translate("previewFailedModal.description").replace("%count%", formatNumber(vm.previewFailedTooManyRows ?? 0))}
            </p>
            <Button variant="danger" size="sm" onClick={vm.closeErrorModal} style={{ paddingLeft: "2rem", paddingRight: "2rem" }}>
              {vm.translate("previewFailedModal.done")}
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <Modal show={!!vm.showNotFoundModal} dialogClassName="modal-sm" centered>
        <Modal.Body>
          <div className="text-center py-4">
            <Icon icon={IconType.CircleWarning} width={66} height={66} style={{ color: "#c3113c", marginBottom: "1.8rem" }} />
            <h4 className="mb-4">{vm.translate("notFoundFailedModal.title")}</h4>
            <p className="mb-4">
              {vm.translate("notFoundFailedModal.descriptionPart1")}{" "}
              <strong>{vm.translate(`notFoundFailedModal.sections.${vm.showNotFoundModal}`)}</strong>{" "}
              {vm.translate("notFoundFailedModal.descriptionPart2")}
            </p>

            <Button variant="danger" size="sm" onClick={vm.closeErrorModal} style={{ paddingLeft: "2rem", paddingRight: "2rem" }}>
              {vm.translate("notFoundFailedModal.done")}
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </LoaderOverlay>
  );
});

registerView(SubLedgerNewReportView, SubLedgerNewReportViewModel);
